import React from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConsentAction, ConsentError, ReasonCode, RoutePath } from '../../constants';
import {
  OnpError,
  checkTokenExpiration,
  getErrorProps,
  getParamsByQueryParamEnum,
  getVinsToConsent,
  getToken,
  getVinsToRevoke,
  redirect,
  spinnerOff,
  spinnerOn,
  getQueryParamString,
  logToRemote,
} from '../../util';
import { REACT_APP_SUBSCRIBER_URI, REACT_APP_SUBSCRIBER_CTX, REACT_APP_SUBSCRIBER_PUT_CONSENT } from '../../config';

import PropTypes from 'prop-types';
import OnStarContainer from '../OnStarContainer';
import FoundVehicle from './FoundVehicle';
import FoundOnStarHome from './FoundOnStarHome';

const grantConsent = async (accessToken, clientId, params, scopeServices, locale, vinsToConsent, vinsToRevoke) => {
  const consentUrl = `${REACT_APP_SUBSCRIBER_URI}/${REACT_APP_SUBSCRIBER_CTX}/${clientId}/${REACT_APP_SUBSCRIBER_PUT_CONSENT}`;
  const consentUrlWithQueryParams = `${consentUrl}?${new URLSearchParams(params)}`;

  const scopes = scopeServices.map(({ scope }) => scope);
  const scopesStr = scopes.join(' ');
  const grantBody = { consents: [] };

  const hasVinsToConsent = vinsToConsent && vinsToConsent.length > 0;
  const hasVinsToRevoke = vinsToRevoke && vinsToRevoke.length > 0;

  const grantConsentObj = { action: ConsentAction.GRANT, scope: scopesStr };
  if (hasVinsToConsent) {
    grantConsentObj.vins = vinsToConsent.map(({ vin }) => vin);
  }
  grantBody.consents.push(grantConsentObj);

  const revokeConsentObj = hasVinsToRevoke
    ? {
        action: ConsentAction.REVOKE,
        scope: scopesStr,
        vins: vinsToRevoke.map(({ vin }) => vin),
      }
    : undefined;

  if (revokeConsentObj) {
    grantBody.consents.push(revokeConsentObj);
  }

  const consentHeaders = { Authorization: `Bearer ${accessToken}`, 'Accept-Language': locale };

  return axios.put(consentUrlWithQueryParams, grantBody, {
    headers: consentHeaders,
  });
};

const handleAuthorizeError = (errorProps, redirectUri, state, redirectTo, history, code) => {
  const { status, moreInfo } = errorProps;

  if (moreInfo) {
    let error = moreInfo.split('.');
    error = error[error.length - 1];

    if (!error) {
      error = ConsentError.serverError;
    }

    logToRemote({
      code,
      level: 'error',
      desc: 'Exception at ConsentConfirmation call',
      msg: ConsentError.serverError,
      src: 'ConsentConfirmation/index.js::handleAuthorizeError::moreInfo',
    });

    redirectTo(redirectUri, { error, state });
  } else if (status === '401' || status === '403') {
    logToRemote({
      code,
      level: 'error',
      desc: 'Exception at ConsentConfirmation call',
      msg: ConsentError.accessDenied,
      src: 'ConsentConfirmation/index.js::handleAuthorizeError::401||403',
    });

    const error = ConsentError.accessDenied;
    redirectTo(redirectUri, { error, state });
  } else {
    logToRemote({
      code,
      level: 'error',
      desc: 'Exception at ConsentConfirmation call',
      msg: 'Unknown error',
      src: 'ConsentConfirmation/index.js::handleAuthorizeError::else',
    });

    history.push({ pathname: RoutePath.error, search: getQueryParamString(), state: errorProps });
  }
};

export const ConsentConfirmation = ({ eligibleVehResp, spinnerRef }) => {
  const { i18n } = useTranslation();
  const history = useHistory();
  const locale = i18n.language;
  const { clientId, redirectUri, state } = getParamsByQueryParamEnum();
  const { ConsentScopeService, profileId, reasonCode } = eligibleVehResp;
  const { scopeServices, name: clientName } = ConsentScopeService;
  const vinsToConsent = getVinsToConsent(eligibleVehResp);
  const vinsToRevoke = getVinsToRevoke(eligibleVehResp);

  const grantAccess = async () => {
    try {
      spinnerOn(spinnerRef);

      const accessToken = await getToken(clientId);
      const isTokenExpired = checkTokenExpiration(accessToken);

      if (isTokenExpired) {
        history.push({ pathname: RoutePath.logout, search: getQueryParamString() });
      } else {
        const params = { profileId };
        const resp = await grantConsent(
          accessToken,
          clientId,
          params,
          scopeServices,
          locale,
          vinsToConsent,
          vinsToRevoke
        );
        const code = resp?.data?.authCode;

        if (!code) {
          throw new OnpError(
            null,
            'Failed to get auth code',
            null,
            null,
            `consent.error.${ConsentError.serverError}`,
            null
          );
        }

        redirect(redirectUri, { code, state });
      }
    } catch (e) {
      const code = e.response?.status || e.code || '500';
      const errorProps = getErrorProps(e);
      handleAuthorizeError(errorProps, redirectUri, state, redirect, history, code);
    } finally {
      spinnerOff(spinnerRef);
    }
  };

  const onAuthorize = () => {
    grantAccess();
  };

  const onCancel = () => {
    redirect(redirectUri, {
      error: ConsentError.accessDenied,
      state,
    });
  };

  const onChangeVehicles = () => {
    history.push({ pathname: RoutePath.eligibleVehicle, search: getQueryParamString() });
  };

  return (
    <OnStarContainer title={i18n.t('papi.general.confirmAuthorization.lable')} clientName={clientName}>
      <section>
        <p className='subheading'>{clientName}</p>
        <p>{i18n.t('papi.general.authorize.message.a', { 0: clientName })}</p>
        <div style={{ marginTop: '2em' }}>
          {reasonCode === ReasonCode.foundVehicles && (
            <FoundVehicle
              vinsToConsent={vinsToConsent}
              scopeServices={scopeServices}
              onAuthorize={onAuthorize}
              onChangeVehicles={onChangeVehicles}
            />
          )}
          {reasonCode === ReasonCode.foundOnStarHome && <FoundOnStarHome onAuthorize={onAuthorize} />}
        </div>
        <div>
          <button id='cancel-btn' className='clear-button' onClick={onCancel}>
            {i18n.t('papi.general.cancel.lable')}
          </button>
        </div>
      </section>
    </OnStarContainer>
  );
};

ConsentConfirmation.propTypes = {
  eligibleVehResp: PropTypes.object,
  spinnerRef: PropTypes.object,
};

export default ConsentConfirmation;
